import { FC, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Flex } from '@rebass/emotion';

import { isNotNullish } from '@app/helpers/predicate';
import TermsAndConditions from '@app/shop/pages/Checkout/Review/TermsAndConditions';
import { H1 } from '@app/shop/components/typography';
import useCart from '@app/shop/hooks/useCart';
import {
  ReviewTable,
  AddressCard,
  ShippingCard,
  Error,
} from '@app/shop/pages/Checkout/Review/Review.styles';
import FormattedMoney from '@app/shop/components/FormattedMoney';
import { ReviewAddressQuery } from '@app/shop/pages/Checkout/Review/__generated__/ReviewAddressQuery';
import FormattedCountryName from '@app/shop/components/FormattedCountryName';
import { Button } from '@app/shop/components/Button';
import CheckoutBackLink from '@app/shop/components/CheckoutBackLink';
import messages from '@app/shop/pages/Checkout/Review/Review.messages';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

const Review: FC = () => {
  const history = useHistory();
  const { language = 'en' } = useParams<{ language?: string }>();

  const {
    items,
    prices,
    loading: cartLoading,
    termsAccepted,
    storeConfig,
  } = useCart();

  const { data, loading: addressLoading } = useQuery<ReviewAddressQuery>(
    REVIEW_ADDRESS_QUERY,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      ssr: false,
    }
  );

  useGTMTracking({ step: 'review', items, prices, storeConfig });

  const [allTermsAccepted, setAllTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const { discounts, appliedTaxes } = prices ?? {};

  const validDiscounts = useMemo(() => discounts?.filter(isNotNullish), [
    discounts,
  ]);

  const validTaxes = useMemo(() => appliedTaxes?.filter(isNotNullish), [
    appliedTaxes,
  ]);

  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  if (cartLoading || addressLoading) return null;
  const billingAddress = data?.magento2?.customerCart?.billingAddress;
  const isVirtual = data?.magento2?.customerCart?.isVirtual;
  const shippingAddress = data?.magento2?.customerCart?.shippingAddresses[0];
  const shippingMethod = shippingAddress?.selectedShippingMethod;
  const isContinueDisabled = allTermsAccepted && termsAccepted;

  return (
    <>
      <H1>
        <FormattedMessage {...messages.title} />
      </H1>
      <ReviewTable alternateRows={false}>
        <thead>
          <tr>
            <th>
              <FormattedMessage {...messages.itemProductHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemQuantityHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemPriceHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemTotalHeader} />
            </th>
          </tr>
        </thead>
        <tbody>
          {items?.map((item, idx) =>
            item ? (
              <tr key={idx}>
                <th scope="row">{item.product.name}</th>
                <td>{item.quantity}</td>
                <td>
                  {includingTax ? (
                    <FormattedMoney {...item.prices?.priceIncludingTax} />
                  ) : (
                    <FormattedMoney {...item.prices?.price} />
                  )}
                </td>
                <td data-testid="test1">
                  {includingTax ? (
                    <FormattedMoney {...item.prices?.rowTotalIncludingTax} />
                  ) : (
                    <FormattedMoney {...item.prices?.rowTotal} />
                  )}
                </td>
              </tr>
            ) : null
          )}
          <tr data-table="review-subtotal">
            <th scope="row" colSpan={3}>
              <FormattedMessage {...messages.subtotal} />
            </th>
            <td>
              {includingTax ? (
                <FormattedMoney {...prices?.subtotalIncludingTax} />
              ) : (
                <FormattedMoney {...prices?.subtotalExcludingTax} />
              )}
            </td>
          </tr>
          {validDiscounts?.map(({ label, amount }) => (
            <tr key={label}>
              <th scope="row" colSpan={3}>
                <FormattedMessage {...messages.discount} />
              </th>
              <td>
                <FormattedMoney
                  currency={amount.currency}
                  value={(amount.value ?? 0) * -1}
                />
              </td>
            </tr>
          ))}
          {validTaxes?.map(({ label, amount }) => (
            <tr key={label}>
              <th scope="row" colSpan={3}>
                {includingTax ? (
                  <FormattedMessage {...messages.inclTax} />
                ) : (
                  <FormattedMessage {...messages.exTax} />
                )}
                {label ? ` (${label})` : null}
              </th>
              <td>
                <FormattedMoney {...amount} />
              </td>
            </tr>
          ))}
          {!isVirtual && (
            <tr>
              <th scope="row" colSpan={3}>
                <FormattedMessage {...messages.shippingHandling} />{' '}
                {shippingMethod?.carrierTitle}
              </th>
              <td data-testid="review-shipping-costs-value">
                <FormattedMoney
                  {...shippingMethod?.[
                    includingTax ? 'amountInclTax' : 'amount'
                  ]}
                />
              </td>
            </tr>
          )}
          <tr data-table="review-total">
            <th scope="row" colSpan={3}>
              <FormattedMessage {...messages.grandTotal} />
            </th>
            <td>
              <FormattedMoney {...prices?.grandTotal} />
            </td>
          </tr>
        </tbody>
      </ReviewTable>
      <Flex
        justifyContent="space-between"
        mt={3}
        mb={3}
        direction="column"
        flexWrap="wrap"
      >
        <AddressCard>
          <Flex flexDirection="column">
            <span>
              <FormattedMessage {...messages.billingTitle} />
            </span>
            {billingAddress?.firstname} {billingAddress?.lastname} <br />
            {billingAddress?.company && (
              <>
                {billingAddress?.company}
                <br />
              </>
            )}
            {billingAddress?.street[0]} <br />
            {billingAddress?.postcode} {billingAddress?.city} <br />
            <FormattedCountryName code={billingAddress?.country.code} />
          </Flex>
        </AddressCard>
        {!isVirtual && (
          <>
            <AddressCard>
              <Flex flexDirection="column">
                <span>
                  <FormattedMessage {...messages.shippingTitle} />
                </span>
                {shippingAddress?.firstname} {shippingAddress?.lastname} <br />
                {shippingAddress?.company && (
                  <>
                    {shippingAddress?.company}
                    <br />
                  </>
                )}
                {shippingAddress?.street[0]} <br />
                {shippingAddress?.postcode} {shippingAddress?.city} <br />
                <FormattedCountryName code={shippingAddress?.country.code} />
              </Flex>
            </AddressCard>
            <ShippingCard>
              <Flex flexDirection="column">
                <span>
                  <FormattedMessage {...messages.shippingMethod} />
                </span>
                {shippingMethod?.carrierTitle}
              </Flex>
            </ShippingCard>
          </>
        )}
      </Flex>

      {termsError && (
        <Error>
          <FormattedMessage {...messages.termsError} />
        </Error>
      )}

      <TermsAndConditions
        onTermsAcceptanceChange={setAllTermsAccepted}
        error={termsError}
      />
      <Flex justifyContent="space-between" alignItems="center" pt={3}>
        {isVirtual ? (
          <CheckoutBackLink to="/:language/shop/checkout/address">
            <FormattedMessage
              {...messages[isVirtual ? 'backAddress' : 'backShipping']}
            />
          </CheckoutBackLink>
        ) : (
          <CheckoutBackLink to="/:language/shop/checkout/delivery">
            <FormattedMessage
              {...messages[isVirtual ? 'backAddress' : 'backShipping']}
            />
          </CheckoutBackLink>
        )}

        <Button
          data-testid="review-continue-button"
          type="primary"
          onClick={() => {
            if (!isContinueDisabled) {
              setTermsError(true);
            } else {
              history.push(
                generatePath('/:language/shop/checkout/payment/', { language })
              );
            }
          }}
        >
          <FormattedMessage {...messages.continue} />
        </Button>
      </Flex>
    </>
  );
};
export default Review;

export const REVIEW_ADDRESS_QUERY = gql`
  query ReviewAddressQuery {
    magento2 {
      customerCart {
        id
        isVirtual
        shippingAddresses {
          firstname
          lastname
          street
          postcode
          city
          company
          country {
            code
          }
          selectedShippingMethod {
            carrierTitle
            amount {
              value
              currency
            }
            amountInclTax {
              value
              currency
            }
          }
        }
        billingAddress {
          firstname
          lastname
          street
          postcode
          city
          company
          country {
            code
          }
        }
      }
    }
  }
`;
